import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  type?: 'group' | 'malware' | 'sandbox'; // For determining section types
}

interface FloatingNavProps {
  items: NavItem[];
}

export function FloatingNav({ items }: FloatingNavProps) {
  // Don't render navigation if there's only one item
  if (items.length <= 1) return null;

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sectionsRef = useRef<Map<string, { top: number, height: number }>>(new Map());
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Function to calculate section positions and determine active section
    const calculateSectionPositions = () => {
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const viewportMiddle = scrollY + (windowHeight / 2);
      
      // Clear the map
      sectionsRef.current.clear();
      
      // Calculate and store positions for all sections
      items.forEach(item => {
        const element = document.getElementById(item.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const top = scrollY + rect.top;
          sectionsRef.current.set(item.id, {
            top,
            height: rect.height
          });
        }
      });
      
      // Find which section contains the middle of the viewport
      let bestMatch = null;
      let minDistance = Infinity;
      
      sectionsRef.current.forEach((position, id) => {
        const sectionMiddle = position.top + (position.height / 2);
        const distance = Math.abs(viewportMiddle - sectionMiddle);
        
        if (distance < minDistance) {
          minDistance = distance;
          bestMatch = id;
        }
      });
      
      if (bestMatch) {
        setActiveSection(bestMatch);
      }
    };

    // Create new observer with a larger root margin to observe elements before they enter viewport
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // If any section becomes visible or invisible, recalculate positions
        if (entries.some(entry => entry.isIntersecting)) {
          calculateSectionPositions();
        }
      },
      {
        rootMargin: "500px 0px 500px 0px", // Increased margin to detect earlier
        threshold: [0, 0.1, 0.5, 1.0]
      }
    );

    // Start observing all sections
    items.forEach(item => {
      const element = document.getElementById(item.id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    // Add scroll event listener for smoother updates
    const handleScroll = () => {
      calculateSectionPositions();
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial calculation
    calculateSectionPositions();

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('scroll', handleScroll);
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, [items]);

  const handleClick = (id: string) => {
    // Set the clicked section as active immediately for better UX
    setActiveSection(id);

    const element = document.getElementById(id);
    if (!element) {
      // If element doesn't exist yet due to lazy loading, scroll to approximate position
      const index = items.findIndex(item => item.id === id);
      if (index > 0) {
        // Find the closest previous section that exists
        let foundElement = null;
        for (let i = index - 1; i >= 0; i--) {
          const prevElement = document.getElementById(items[i].id);
          if (prevElement) {
            foundElement = prevElement;
            break;
          }
        }
        
        if (foundElement) {
          // Scroll to the previous section first
          foundElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Then try to find and scroll to the target element after a delay
          // This gives time for lazy loaded content to appear
          if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
          }
          
          clickTimeoutRef.current = setTimeout(() => {
            const targetElement = document.getElementById(id);
            if (targetElement) {
              targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              // If still not found, scroll a bit more
              window.scrollBy({ top: 500, behavior: 'smooth' });
            }
          }, 300);
        } else {
          // If no previous section, just scroll down a bit
          window.scrollBy({ top: index * 500, behavior: 'smooth' });
        }
      }
    } else {
      // Element exists, scroll to it
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleNav = () => {
    setIsExpanded(!isExpanded);
  };

  // Determine section breaks by checking type changes
  let lastType: string | undefined = undefined;

  return (
    <div 
      className={cn(
        "fixed right-0 top-1/2 z-40 -translate-y-1/2 hidden md:flex transition-transform duration-300",
        isExpanded ? "translate-x-0" : "translate-x-[calc(100%-32px)]"
      )}
    >
      <button
        onClick={toggleNav}
        className="h-16 w-8 flex items-center justify-center bg-card border-t border-b border-l rounded-l-md border-border"
        aria-label={isExpanded ? "Collapse navigation" : "Expand navigation"}
      >
        {isExpanded ? (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      
      <nav className="rounded-l-lg border border-border bg-card shadow-lg py-2 border-r-0">
        <ul className="flex flex-col py-1 w-48">
          {items.map((item, index) => {
            // Check if we need to add a separator before this item
            const showSeparator = index > 0 && item.type !== lastType;
            // Update the last type for the next iteration
            lastType = item.type;
            
            return (
              <li key={item.id} className="w-full">
                {showSeparator && (
                  <div className="mx-2 my-1 border-t border-border"></div>
                )}
                <button
                  className={cn(
                    "w-full text-sm px-4 py-2 rounded-md transition-colors text-left",
                    "hover:bg-accent hover:text-accent-foreground truncate",
                    activeSection === item.id
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground"
                  )}
                  onClick={() => handleClick(item.id)}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}